import { ActionReference, ClickGameState, GameObjectReference, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";

/** CSS affecting the {@link StartComponent} */
const styles: string = css`
    :host {
        font-family: "Onesize";
        width: 100%;
        max-width: 1000px;
        height: 100%;
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto calc(90vh + 10px) minmax(calc(90vh + 10px), 1fr) auto;
        grid-column-gap: 0px;
        grid-row-gap: 0px;
    }

    .title {
        text-align: center;
        margin-top: 10px;
        overflow: auto;
        min-height: 40px;
    }

    .content {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        margin-top: 10px;
    }

    /* TODO: fix image scaling its an issue */
    .content img {
        width: auto;
        height: 600px;
        image-rendering: pixelated;
    }

    .content img:nth-child(n + 2) {
        position: absolute;
    }

    .content  area {
        cursor: pointer;
    }
`;

/**
 * Represents the Start page
 */
export class StartComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: ClickGameState;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const clickGame: GameState = await this._gameRouteService.getClickGame();

        console.log(clickGame);

        this.updateGameState(clickGame);
    }

    /**
     * Update the start to the provided game state
     *
     * @param state Game state to update the start to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderTitle()}
            ${this.renderContent()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private renderTitle(): string {
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (roomName) {
            return `<div class="title">${roomName}</div>`;
        }

        return "";
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderContent(): string | HTMLElement {
        const roomImages: string[] | undefined = this._currentGameState?.roomImages;

        if (roomImages && roomImages.length > 0) {
            return html`
                <div class="content" id="content" >
                    ${roomImages.map(url => `<img src="/assets/img/rooms/${url}.png" usemap="#image-clicks" />`).join("")}

                    <map name="image-clicks">
                        ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}    
                        ${this._currentGameState?.objects.map(button => this.renderGameObjectButton(button))}
                    </map>
                </div>
                
            `;
        }

        return "";
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <area coords=${button.coords} shape="rect">
        `;

        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        console.log(button);

        if (button.coords) {
            const element: HTMLElement = html`
                <area coords=${button.coords} shape="rect">
            `;

            element.addEventListener("click", () => this.handleClickGameObject(button));

            return element;
        }

        const element: HTMLElement = html`
                <area coords="" shape="rect">
            `;

        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeClickAction(button.alias);

        if (state === undefined) {
            return;
        }

        this.updateGameState(state);
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        console.log(button.action, button.alias, this._selectedGameObjectButtons);

        this._selectedGameObjectButtons.add(button);

        const actionName: string = button.action ? button.action : "";
        const state: GameState | undefined = await this._gameRouteService.executeClickAction(
            actionName,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        if (state === undefined) {
            return;
        }

        this.updateGameState(state);
    }
}
